<template>
  <component
    :is="variant === 'card' ? VCard : variant === 'none' ? PassThrough : 'div'"
    v-bind="variant === 'card' ? { variant: 'flat', border: true } : {}">
    <editable-key-value
      v-bind="$attrs"
      :class="{ 'pa-4': variant === 'card' }"
      :can-edit="isEditable && Boolean(formField.field?.id)"
      :is-editing="isEditing"
      :always-show-edit-button="alwaysShowEditButton"
      :edit-button-props="{ disabled: isBusy }"
      :cancel-button-props="{ disabled: isBusy }"
      :save-button-props="{ disabled: !canSave || isBusy, loading: isSaving }"
      @edit="isEditing = true"
      @cancel="handleCancel"
      @save="handleSave">
      <template #label>
        {{ formField.label }}
        <span v-if="formField.required" class="typography-body-sm-bold text-red">*</span>
      </template>
      <template #value>
        <span v-if="formField.formattedValue" v-html="formField.formattedValue"></span>
        <presenter-value-fallback v-else />
      </template>
      <template #editing-value>
        <v-form ref="form">
          <component
            :is="getComponent(formField.field.type)"
            v-bind="getComponentProps(formField.field.type)"
            v-model="localValue"
            :field="customField" />
        </v-form>
      </template>
    </editable-key-value>
  </component>
</template>
<script>
import {
  CustomFieldType,
  getCustomFieldTestId,
  isValidCustomField,
  json,
  sanitizeCustomFieldValue
} from '@nova/core';
import { isString } from 'class-validator';
import { isNil } from 'lodash';
import { ref, defineComponent, computed, watch } from 'vue';
import { VCard } from 'vuetify/components';

import { EditableKeyValue } from '@satellite/components/elements/key_value';
import PresenterValueFallback from './presenters/PresenterValueFallback.vue';

/**
 * Renderless wrapper: renders its default slot and no element of its own. Used as the `none`
 * variant's `<component :is>` target, where the `editable-key-value` itself must be the parent
 * list's child — a real element there (even with `display: contents`) still breaks the parent
 * list's direct-child selectors and `:nth-child` counting. The `plain` variant instead renders a
 * block `div` that is the list row. Vue's `Fragment` symbol cannot stand in here: as an `:is`
 * target it receives the slots as an object and drops the children entirely.
 */
const PassThrough = defineComponent({
  name: 'PassThrough',
  inheritAttrs: false,
  setup(_, { slots }) {
    return () => slots.default?.();
  }
});

export default defineComponent({
  name: 'CustomFormsMultipleTriggerFormField',
  components: {
    EditableKeyValue,
    PresenterValueFallback
  },
  /**
   * The `none` variant renders a fragment root, which cannot inherit attributes, so the caller's
   * `data-testid` is forwarded onto the `editable-key-value` explicitly instead.
   */
  inheritAttrs: false,
  props: {
    /*
     * FormField entity instace from custom forms
     * An extra property called dataId is the customformdata.id
     * Used to update the existing data, when not present,
     * just create a new instance of custom form data
     */
    formField: {
      type: Object,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'plain',
      validator(value) {
        return ['card', 'plain', 'none'].includes(value);
      }
    },
    componentsMapping: {
      type: Object,
      required: false,
      default: () => ({})
    },
    timezone: {
      type: String,
      required: false
    },
    militaryTimeEnabled: {
      type: Boolean,
      required: false
    },
    alwaysShowEditButton: {
      type: Boolean,
      default: false
    },
    /** The parent form is (re)fetching the values, so they may change under an edit. */
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:formField'],
  setup(props, { emit }) {
    const isSaving = ref(false);
    const isEditing = ref(false);
    const localValue = ref(null);

    const isBusy = computed(() => isSaving.value || props.isLoading);

    /**
     * Normalizes a stored value to the format expected by isValidCustomField and the API.
     *
     * DropDownMultiSelect / MultiDocument: CustomFormData stores the array as a JSON string.
     * Parse it back so array validators pass and the API receives the correct type.
     */
    const normalizeValueForType = (type, value) => {
      if (
        (type === CustomFieldType.DropDownMultiSelect || type === CustomFieldType.MultiDocument) &&
        isString(value)
      ) {
        const parsed = json.safeParse(value);
        if (Array.isArray(parsed)) {
          return sanitizeCustomFieldValue(type, parsed);
        }
      }
      if (type === CustomFieldType.DropDownMultiSelect) {
        return sanitizeCustomFieldValue(type, value);
      }
      return value;
    };

    // This is a mapping to the custom field components
    const customField = computed(() => {
      return {
        name: props.formField.id,
        order: props.formField.order,
        value: props.formField.value,
        required: props.formField.required,
        placeholder: props.formField.field.placeholder,
        type: props.formField.field.type,
        dropDownValues: props.formField.field.extraFields?.dropDownValues,
        minLengthOrValue: props.formField.field.extraFields?.minLengthOrValue,
        maxLengthOrValue: props.formField.field.extraFields?.maxLengthOrValue
      };
    });

    const canSave = computed(() => {
      let isFilled = true;

      const normalizedValue = normalizeValueForType(customField.value.type, localValue.value);
      const field = { ...customField.value, value: normalizedValue };

      /**
       * An optional bool can be cleared back to unanswered. isValidCustomField only accepts true or
       * false, but removing a stored answer is a real change: a PATCH with a null value. With no
       * stored answer there is nothing to clear, and nothing to create either.
       */
      if (field.type === CustomFieldType.Bool && !field.required && isNil(field.value)) {
        return Boolean(props.formField.dataId);
      }

      if (field.type !== CustomFieldType.Bool) {
        if (field.required && !field.value) {
          isFilled = false;
        }
      }
      return isFilled && isValidCustomField(field);
    });

    const getComponent = type => {
      return props.componentsMapping[type]?.component ?? 'v-text-field';
    };

    const getComponentProps = type => {
      // Shallow-clone to avoid mutating the original componentsMapping props object across renders
      const componentProps = { ...(props.componentsMapping[type]?.props || {}) };

      componentProps.testId = getCustomFieldTestId({
        formFieldId: props.formField.id,
        triggerId: props.formField.triggerId
      });
      componentProps.timezone = props.timezone;
      componentProps.militaryTimeEnabled = props.militaryTimeEnabled;
      componentProps.required = customField.value.required ?? false;
      componentProps.placeholder = customField.value.placeholder;

      // This is meant for Luna which has a different component for the custom fields
      if (getComponent(type)?.name === 'BasicFormField') {
        componentProps.fieldName = props.formField.id;
        componentProps.hint = customField.value.description;

        // Merge into existing fieldProps rather than replacing so any type-specific props
        // defined in the mapping (e.g. { type: 'text' }) are preserved.
        componentProps.fieldProps = {
          ...(componentProps.fieldProps || {}),
          timezone: props.timezone,
          militaryTimeEnabled: props.militaryTimeEnabled,
          // DropDownField needs multiple=true to emit arrays for multiselect fields
          ...(type === CustomFieldType.DropDownMultiSelect && { multiple: true }),
          // DocumentUpload needs isMultiple=true so it allows uploading several files
          ...(type === CustomFieldType.MultiDocument && { isMultiple: true }),
          // Pass the current value so DocumentUpload initializes with existing files
          // rendered, making them visible and removable.
          // Luna's DocumentUpload emits string[] even for a single-file field, so
          // normalise Document values from array → string here (app-specific behaviour).
          ...((type === CustomFieldType.Document || type === CustomFieldType.MultiDocument) && {
            value: (() => {
              const v = normalizeValueForType(type, localValue.value);
              return type === CustomFieldType.Document && Array.isArray(v) ? (v[0] ?? null) : v;
            })()
          })
        };

        if (customField.value.dropDownValues) {
          componentProps.options = [...customField.value.dropDownValues];
        }
      }

      return componentProps;
    };

    const handleSave = async () => {
      isSaving.value = true;
      try {
        const formData = {
          id: props.formField.dataId,
          formFieldId: props.formField.id,
          objectId: props.formField.objectId,
          triggerId: props.formField.triggerId,
          entityName: props.formField.entityName ?? 'appointment',
          warehouseId: props.formField.warehouseId,
          value: normalizeValueForType(customField.value.type, localValue.value)
        };

        if (formData.id) {
          await window.axios.patch(`custom-forms/form-data/${formData.id}`, {
            value: formData.value
          });
        } else {
          await window.axios.post(`custom-forms/form-data`, formData);
        }

        isEditing.value = false;
        emit('update:formField', { ...formData, label: props.formField.label });
      } catch (error) {
        console.error('Error saving custom field:', error);
      } finally {
        isSaving.value = false;
      }
    };

    const handleCancel = () => {
      isEditing.value = false;
      localValue.value = normalizeValueForType(props.formField.field?.type, props.formField.value);
    };

    watch(
      () => props.formField.value,
      newValue => {
        localValue.value = normalizeValueForType(props.formField.field?.type, newValue);
      },
      { immediate: true, deep: true }
    );

    return {
      PassThrough,
      VCard,
      isBusy,
      isSaving,
      isEditing,
      handleCancel,
      handleSave,
      customField,
      getComponent,
      getComponentProps,
      localValue,
      canSave
    };
  }
});
</script>
